import { g, layers, LayerClass, newDocument } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { SerializedProject } from './types';

export class ProjectSerializer {
  static async saveProject(): Promise<string> {
    const project: SerializedProject = {
      version: 2,
      width: g.image_width,
      height: g.image_height,
      backgroundColor: g.image_bg_color,
      layers: [],
    };

    for (const layer of layers) {
      let dataUrl: string;
      const canvas = layer.canvas as any;
      if (typeof canvas.toDataURL === 'function') {
        dataUrl = canvas.toDataURL('image/png');
      } else {
        const tmp = document.createElement('canvas');
        tmp.width = canvas.width;
        tmp.height = canvas.height;
        const tmpCtx = tmp.getContext('2d');
        if (!tmpCtx) throw new Error('Could not get 2D context for temp canvas');
        tmpCtx.drawImage(canvas, 0, 0);
        dataUrl = tmp.toDataURL('image/png');
      }

      project.layers.push({
        name: layer.name,
        visible: layer.visible,
        opacity: layer.opacity,
        blendMode: layer.blendMode,
        locked: layer.locked,
        type: layer.type,
        data: dataUrl,
        shapes: layer.shapes,
        textData: layer.textData,
      });
    }

    return JSON.stringify(project);
  }

  static async loadProject(jsonString: string): Promise<boolean> {
    try {
      const project = JSON.parse(jsonString) as SerializedProject;
      if (!project.width || !project.height || !project.layers) {
        throw new Error('Invalid project file format');
      }

      console.log(`[Serializer] Loading Project v${project.version || 1}...`);
      g.image_width = project.width;
      g.image_height = project.height;

      // Notify UI to resize
      EventBus.emit('PROJECT_RESIZE', { width: g.image_width, height: g.image_height });

      layers.length = 0;
      for (const layerData of project.layers) {
        const newLayer = new LayerClass(layerData.name, g.image_width, g.image_height);
        newLayer.visible = layerData.visible;
        newLayer.opacity = layerData.opacity;
        newLayer.blendMode = layerData.blendMode ?? 'source-over';
        newLayer.locked = layerData.locked ?? false;
        newLayer.type = layerData.type ?? 'raster';
        newLayer.shapes = layerData.shapes;
        newLayer.textData = layerData.textData;

        if (layerData.data) {
            await new Promise<void>((resolve, reject) => {
              const img = new Image();
              img.onload = () => {
                (newLayer.ctx as any).drawImage(img, 0, 0);
                resolve();
              };
              img.onerror = () => reject(new Error(`Failed to load layer image: ${layerData.name}`));
              img.src = layerData.data;
            });
        }
        layers.push(newLayer);
      }

      if (layers.length === 0) g.initDefaultLayer();
      g.activeLayerIndex = Math.max(0, layers.length - 1);

      // Notify UI of changes
      EventBus.emit('PROJECT_LOADED', { layerCount: layers.length });
      
      console.log('[Serializer] Project loaded successfully');
      return true;
    } catch (err) {
      console.error('[Serializer] Failed to load project:', err);
      return false;
    }
  }
}
